import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useRealtime } from '@/hooks/useRealtime';
import { useAuth } from '@/hooks/useAuth';
import AuthGuard from '@/components/feature/AuthGuard';
import OrganizerLayout from '../components/OrganizerLayout';

interface Announcement {
  id: string;
  title: string;
  content: string;
  created_by: string | null;
  published_at: string | null;
  created_at: string;
  author_name?: string;
}

export default function OrganizerAnnouncements() {
  const { profile } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', content: '' });
  const [saving, setSaving] = useState(false);

  const loadAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('announcements')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;

      const userIds = [...new Set((data || []).map((a) => a.created_by).filter(Boolean))];
      let profilesMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds as string[]);
        if (profiles) {
          profiles.forEach((p) => { profilesMap[p.id] = p.full_name || 'Unknown'; });
        }
      }

      const enriched = (data || []).map((a) => ({
        ...a,
        author_name: a.created_by ? (profilesMap[a.created_by] || 'Unknown') : 'System',
      }));
      setAnnouncements(enriched);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  useRealtime<Announcement>({
    table: 'announcements',
    onAny: () => loadAnnouncements(),
  });

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await supabase.from('announcements').update({
          title: form.title.trim(),
          content: form.content.trim(),
        }).eq('id', editId);
      } else {
        await supabase.from('announcements').insert({
          title: form.title.trim(),
          content: form.content.trim(),
          published_at: new Date().toISOString(),
          created_by: profile?.id,
        });
      }
      setShowModal(false);
      setEditId(null);
      setForm({ title: '', content: '' });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await supabase.from('announcements').delete().eq('id', id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const openEdit = (a: Announcement) => {
    setEditId(a.id);
    setForm({ title: a.title, content: a.content });
    setShowModal(true);
  };

  return (
    <AuthGuard requiredRole="organizer">
      <OrganizerLayout title="Announcements" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/organizer' }, { label: 'Announcements' }]}>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="cursor-pointer"><i className="ri-close-line"></i></button>
          </div>
        )}

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-lg font-bold text-foreground-950">Manage Announcements</h2>
          <button onClick={() => { setEditId(null); setForm({ title: '', content: '' }); setShowModal(true); }} className="px-4 py-2.5 rounded-lg bg-secondary-500 hover:bg-secondary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line"></i> New Announcement
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 rounded-full border-2 border-secondary-500 border-t-transparent animate-spin"></div>
          </div>
        ) : announcements.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-megaphone-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-400 text-sm">No announcements available</p>
          </div>
        ) : (
          <div className="space-y-4">
            {announcements.map((a) => (
              <div key={a.id} className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">{a.title}</h3>
                    <p className="text-sm text-foreground-600 mb-3">{a.content}</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs text-foreground-400">Posted: {new Date(a.published_at || a.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      <span className="text-xs text-foreground-400">By: {a.author_name}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(a)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary-50 text-foreground-400 hover:text-secondary-600 transition-colors cursor-pointer" title="Edit">
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button onClick={() => handleDelete(a.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-600 transition-colors cursor-pointer" title="Delete">
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowModal(false)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-background-200/70 p-5 flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-foreground-950">{editId ? 'Edit' : 'New'} Announcement</h3>
                <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Title</label>
                  <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none" placeholder="Announcement title..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Content</label>
                  <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={5} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none resize-none" placeholder="Write your announcement..."></textarea>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="px-5 py-2 rounded-lg bg-secondary-500 hover:bg-secondary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">
                    {saving ? 'Saving...' : editId ? 'Save Changes' : 'Publish'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </OrganizerLayout>
    </AuthGuard>
  );
}